/**
 * Step 13 — progression: XP curve, level-up gating, offer generation, reroll,
 * pick application, evolution gating.
 *
 * GDD: §9.3 (level curve), §9.4 (card pipeline, duplicate protection, reroll,
 * evolutions, pool-exhaust fallbacks), §9.9.1 (starter pool), §6.5 (synergy and
 * counter-synergy flagging), §13.1.6 (plain-language deltas), §11.11 (number format).
 *
 * Determinism: every offer, reroll and rarity roll consumes `w.rng.loot` and nothing
 * else, in a fixed order. Same seed + same picks ⇒ identical card sequences.
 */

import { cfg } from '../core/config';
import {
  EVOLUTIONS,
  MAX_WEAPON_LEVEL,
  PASSIVES,
  WEAPONS,
  evolutionById,
  passiveById,
  resolveWeaponStats,
  weaponById,
  type WeaponStats,
} from '../core/content';
import type { World } from './world';
import {
  counterEffectText,
  counterEfficiency,
  counterPartnerFor,
  displayNameOf,
  passiveMagnitudePerRank,
  passiveMaxRankOf,
  passiveStatKey,
  passiveStatUnit,
  recomputeStats,
  synergyEffectText,
  synergyPartnerFor,
  titleFromId,
} from './build';
import { HOLD_PASSIVE_ID, holdNewDelta, holdRankDelta, resetHold, stepHold } from './hold';

export interface OfferCard {
  id: string;
  kind: 'weapon' | 'passive' | 'evolution' | 'active';
  display_name: string;
  level_from: number;
  level_to: number;
  /** §13.1.6 plain-language delta. Never empty. */
  delta_text: string;
  /** §6.5 synergy or counter line; '' when the pairing is neutral. */
  synergy_text: string;
  /** §6.5 dead-pairing flag; greys the synergy line in the UI. */
  counter_synergy: boolean;
  /** Stable content key; R6/R8 map it to a pixel icon. */
  icon: string;
}

/** spine §5 loadout limits. */
export const MAX_WEAPON_SLOTS = 4;
export const MAX_PASSIVE_SLOTS = 4;
export const MAX_PASSIVE_RANK = 5;
export const MAX_EVOLUTIONS = 4;

/** §9.4.1 draw weights. */
export const WEIGHT_UPGRADE = 0.65;
/** §9.4.1 rarity split for NEW-item cards only. */
export const RARITY_COMMON = 0.70;
export const RARITY_RARE = 0.95;

/** §9.4.5 pool-exhaust fallbacks. */
export const FALLBACK_MOTE_CACHE = 'fallback.mote-cache';
export const FALLBACK_HULL_PATCH = 'fallback.hull-patch';
/**
 * §9.4.5 lists two fallbacks but the draw is three cards wide, so a fully exhausted
 * pool needs a third distinct option. It is a second Mote Cache with its own ID, which
 * keeps "no duplicate ID in one draw" true without inventing a new reward.
 */
export const FALLBACK_MOTE_CACHE_2 = 'fallback.mote-cache-2';
export const FALLBACK_MOTE_CACHE_XP = 50;
export const FALLBACK_HULL_PATCH_HP = 20;

/**
 * §6.6 — the two boons that rewrite what a bolt does. Both state a count rather than a stat
 * multiplier, so both carry their own §13.1.6 copy below instead of the generic sentence.
 */
export const SPLIT_PASSIVE_ID = 'passive.mitotic-bloom';
export const CASCADE_PASSIVE_ID = 'passive.cascade-ganglion';

/**
 * §9.9.1 — content available on run 1. Everything else is achievement-gated (§9.9.2);
 * the integrator adds unlocked IDs from the save file before the run starts.
 */
export const STARTER_POOL: readonly string[] = [
  'weapon.spear-jelly-lance',
  'weapon.krill-scatter',
  'weapon.copepod-halo',
  'weapon.atolla-nova',
  'passive.photophore-amplifier',
  'passive.twitch-ganglia',
  'passive.current-rider-fin',
  'passive.chitin-laminate',
  'passive.vipertooth-hone',
  'passive.bristlemouth-instinct',
  'passive.jet-siphon',
  'passive.grazer-gut',
  // AMD-39 — the Holdfast is a starter, not an unlock. §01 asks for several viable endgame
  // builds, and a build identity you can only meet after an achievement is not an option
  // the first ten runs get to weigh.
  HOLD_PASSIVE_ID,
  // §6.6 — both projectile boons are starters for the same reason. They are the only two
  // passives that change what a bolt *does* rather than how big its number is, and a run that
  // opens on the Lance or the Krillscatter needs at least one of those on the table.
  SPLIT_PASSIVE_ID,
  CASCADE_PASSIVE_ID,
];

/**
 * §9.9.1 — the three weapons a run may open on.
 *
 * Not three flavours of the same bolt: each one asks for a different first ten seconds and
 * carries a different projectile cost, which is what makes the opening a real choice rather
 * than a colour swatch.
 *
 *  - the Lance is aimed for you and reaches 62 u. You kite, you pick, you spend one bolt a
 *    second. It spawns the fewest bodies of the three, which is what makes it the clean
 *    Mitotic Bloom carrier: one bolt in, four shards out, and the budget never notices.
 *  - the Krillscatter is a 28° cone with 22 u of reach on `forward-cone` targeting, so it
 *    shoots where your nose is pointing and nowhere else. You have to drive at things. Twelve
 *    pellets a volley at max level makes it the stress case for every projectile boon in the
 *    game, and the per-frame shard budget exists because of this weapon.
 *  - the Copepod Halo fires nothing at all. Three orbiters at 6 u, no targeting, no
 *    acquisition, no cooldown: you kill by putting your hull where enemies are. It is the
 *    control case for the two boons — neither one touches it, which is exactly why a build
 *    that opens here goes looking for a different half of the passive pool.
 *
 * All three are §9.9.1 starters already and all three sit at ~10.5 DPS at level 1 by their own
 * acceptance criteria, so this is a choice of shape, not of strength.
 */
export const STARTER_WEAPONS: readonly string[] = [
  'weapon.spear-jelly-lance',
  'weapon.krill-scatter',
  'weapon.copepod-halo',
];

/** §9.9.1 — the default when no choice was made (headless harnesses, an unmigrated save). */
export const DEFAULT_STARTER_WEAPON = STARTER_WEAPONS[0];

export function isStarterWeapon(id: string): boolean {
  return contains(STARTER_WEAPONS, id);
}

/**
 * Put one of {@link STARTER_WEAPONS} in slot 0 at level 1 and make it the focus slot.
 *
 * The integrator owns *when* a run opens; this owns *what a started run looks like*, so the
 * three-way choice cannot drift out of sync with the pool it draws from. An id that is not a
 * starter falls back to {@link DEFAULT_STARTER_WEAPON} rather than opening the run unarmed.
 */
export function grantStarterWeapon(w: World, id: string): void {
  const defId = isStarterWeapon(id) ? id : DEFAULT_STARTER_WEAPON;
  const p = w.player;
  const slot = p.weapons[0];
  slot.def_id = defId;
  slot.level = 1;
  slot.evolved = false;
  slot.cooldown_until = 0;
  slot.last_target_id = 0;
  slot.phase = 0;
  slot.slot = 0;
  const def = weaponById.get(defId);
  slot.policy = def === undefined ? 'nearest' : def.targeting_policy;
  p.weapon_count = 1;
  p.focus_slot = 0;
}

const unlocked = new Set<string>(STARTER_POOL);

/** Replace the unlocked set (save load). Unlock ≠ power (§9.9). */
export function setUnlocked(ids: readonly string[]): void {
  unlocked.clear();
  for (let i = 0; i < ids.length; i++) unlocked.add(ids[i]);
}

export function unlockContent(id: string): void {
  unlocked.add(id);
}

/** Sandbox/test helper: every weapon and passive in the pool. */
export function unlockAll(): void {
  for (let i = 0; i < WEAPONS.length; i++) unlocked.add(WEAPONS[i].id);
  for (let i = 0; i < PASSIVES.length; i++) unlocked.add(PASSIVES[i].id);
}

export function isUnlocked(id: string): boolean {
  return unlocked.has(id);
}

// --- §9.3.1 XP curve ---

/**
 * `xp_req(L) = round(10 + 9·(L−1) + 0.95·(L−1)²)` — 30 level-ups over a 20-minute run
 * (C(31) = 12,346 XP, spine §6).
 */
export function xpRequired(level: number): number {
  const n = level - 1;
  return Math.floor(10 + 9 * n + 0.95 * n * n + 0.5);
}

/** Cumulative XP needed to reach `level` from level 1. */
export function xpCumulative(level: number): number {
  let sum = 0;
  for (let l = 1; l < level; l++) sum += xpRequired(l);
  return sum;
}

// --- §9.3.3 XP supply schedule (AMD-45) ---

/**
 * Level-ups the curve is meant to buy per minute. 1.5 × 20 minutes = the 30 level-ups
 * §9.3.1 sizes `xp_req` for, so the schedule below is the curve read forwards instead of
 * backwards.
 */
export const LEVELS_PER_MIN = 1.5;

/**
 * Last minute the schedule is authored for. §8.13's sovereign phase holds the §8.2 curve
 * flat after 20:00 and the schedule holds with it, so a run that overruns keeps the same
 * level pace instead of inheriting the sovereign minute's much smaller budget.
 */
export const XP_SUPPLY_LAST_MIN = 19;

/**
 * **The run's XP supply for one whole minute, in base mote value.**
 *
 * This is the number AMD-45 exists for. Before it, XP supply was `budget_per_min / 4` —
 * the §8.2 threat curve wearing a different unit — because every archetype's
 * `drops.xp_motes` is a fixed fraction of its `threat_score`. Difficulty and player power
 * were literally the same dial: doubling the §8.2 curve doubled the XP the same minute
 * paid out, and the Lumensurge multiplied the product on top of that. Measured, doubling
 * the budget took a patrol pilot from level 5 to level 32 and from 6.7 to 13.6 minutes.
 *
 * Here the supply is a function of the clock and of nothing else. `pickups.ts` divides the
 * minute's realized threat back out of every drop, so a Descent tier, an elite wave, a
 * hunt ramp or any future anti-kite ramp adds pressure and adds **zero** XP. What is left
 * to the player is the only thing that should have been theirs: how much of the minute's
 * supply they actually kill and collect.
 *
 * The shape is `LEVELS_PER_MIN × xp_req` evaluated on the curve's own continuous form, so
 * "the supply for minute m is the price of the levels minute m is meant to buy" is true by
 * construction rather than by a fitted table.
 */
export function xpSupplyForMinute(minute: number): number {
  const m = minute < 0 ? 0 : minute > XP_SUPPLY_LAST_MIN ? XP_SUPPLY_LAST_MIN : minute;
  const n = LEVELS_PER_MIN * m;
  return LEVELS_PER_MIN * (10 + 9 * n + 0.95 * n * n);
}

/** Cumulative authored supply through the end of `minute`. The run's XP ceiling. */
export function xpSupplyCumulative(minute: number): number {
  let sum = 0;
  for (let m = 0; m <= minute; m++) sum += xpSupplyForMinute(m);
  return sum;
}

// --- per-world progression state ---

interface ProgressionState {
  pending_levels: number;
  offers: OfferCard[] | null;
  /** IDs shown in the draw that a reroll discarded (§9.4.3 immediate-redraw exclusion). */
  excluded: string[];
  rerolled_this_pause: boolean;
  evolutions_taken: number;
  active_level: number;
  initialized: boolean;
}

const STATES = new WeakMap<World, ProgressionState>();

function stateOf(w: World): ProgressionState {
  let st = STATES.get(w);
  if (st === undefined) {
    st = {
      pending_levels: 0,
      offers: null,
      excluded: [],
      rerolled_this_pause: false,
      evolutions_taken: 0,
      active_level: 0,
      initialized: false,
    };
    STATES.set(w, st);
  }
  return st;
}

/** Initialise the XP curve and stats for a fresh run. Idempotent per world+reset. */
export function resetProgression(w: World): void {
  const st = stateOf(w);
  st.pending_levels = 0;
  st.offers = null;
  st.excluded.length = 0;
  st.rerolled_this_pause = false;
  st.evolutions_taken = 0;
  st.active_level = 0;
  st.initialized = true;
  w.player.level = 1;
  w.player.xp = 0;
  w.player.xp_to_next = xpRequired(1);
  w.player.rerolls_used = 0;
  resetHold(w);
  recomputeStats(w);
}

export function currentOffers(w: World): OfferCard[] | null {
  return stateOf(w).offers;
}

export function pendingLevelUps(w: World): number {
  return stateOf(w).pending_levels;
}

export function evolutionsTaken(w: World): number {
  return stateOf(w).evolutions_taken;
}

export function rerollsRemaining(w: World): number {
  return Math.max(0, cfg.get('progression.reroll_max') - w.player.rerolls_used);
}

// --- §11.11 number formatting ---

/** Integers plain; >999 abbreviates (`1.2k`); fractions to at most 2 decimals. */
export function formatNumber(v: number): string {
  const a = Math.abs(v);
  if (a >= 1000) {
    const k = Math.round((v / 1000) * 10) / 10;
    return String(k) + 'k';
  }
  if (Number.isInteger(v)) return String(v);
  return String(Math.round(v * 100) / 100);
}

function signed(v: number): string {
  return v >= 0 ? '+' + formatNumber(v) : '-' + formatNumber(-v);
}

// --- plain-language tables (§13.1.6: never print a raw stat key) ---

const WEAPON_BLURBS: Record<string, string> = {
  'weapon.spear-jelly-lance': 'fires a piercing lance at the nearest enemy.',
  'weapon.krill-scatter': 'fires a 5-pellet fan at enemies in front of you.',
  'weapon.barreleye-gaze': 'locks a beam onto the toughest enemy and burns it down.',
  'weapon.copepod-halo': 'spins orbiters around you that hurt whatever they touch.',
  'weapon.atolla-nova': 'pulses a ring that damages and shoves everything close.',
  'weapon.angler-lantern-mine': 'lobs armed lanterns into crowds; they detonate on contact.',
  'weapon.siphonophore-chain-whip': 'arcs a chain that jumps between nearby enemies.',
  'weapon.bobtail-scion': 'summons squidlets that dive at enemies on their own.',
  'weapon.brine-pool': 'pours a pool that damages and slows enemies standing in it.',
  'weapon.vampire-shroud': 'holds a barrier that eats one hit, then pulses damage around you.',
  'weapon.gulper-maw': 'replaces Dash: lunge through enemies, damaging everything in the jaw arc.',
  'weapon.loosejaw-searchlight': 'sweeps a rotating beam around you at all times.',
};

const PASSIVE_LABELS: Record<string, string> = {
  damage_pct: 'damage',
  attack_speed_pct: 'attack speed',
  move_speed_pct: 'move speed',
  max_hp_flat: 'max health',
  armor_flat: 'armor',
  xp_gain_pct: 'XP gain',
  pickup_radius_pct: 'pickup range',
  crit_chance_pct: 'critical chance',
  crit_damage_pct: 'critical damage',
  area_pct: 'effect size',
  duration_pct: 'effect duration',
  projectile_speed_pct: 'projectile speed',
  knockback_pct: 'knockback',
  regen_hp_per_s: 'health regenerated per second',
  dash_cooldown_s: 'dash cooldown',
  surge_drop_tiers: 'Lumensurge lost when you are hit',
  projectile_count_add: 'chance of an extra projectile per volley',
  summon_damage_pct: 'summon damage',
  aura_damage_amp_pct: 'damage enemies near you take',
  overheal_shield_cap_flat: 'barrier from overhealing',
  kill_streak_damage_pct: 'damage per kill stack',
  focus_aim_damage_pct: 'damage inside your focus-aim cone',
  hold_payout_pct: 'reward for holding your ground in a crowd',
  split_shards_add: 'shards a spent bolt bursts into',
  cascade_levels_add: 'levels the ripple spreads through',
};

// --- §6.6 projectile boons: their own copy (§13.1.6 never prints a raw stat key) ---

/** Shards a Mitotic Bloom of `rank` produces. Mirrors `build.ts`; rank 1 is already a burst. */
function splitShardsAt(rank: number): number {
  return rank <= 0 ? 0 : Math.min(rank + 1, 4);
}

/** Bodies a Cascade Ganglion of `rank` reaches: 1, 3, 6, 10 as the fan widens 1 · 2 · 3 · 4. */
function cascadeBodiesAt(rank: number): number {
  const levels = rank <= 0 ? 0 : Math.min(rank, 4);
  return (levels * (levels + 1)) / 2;
}

function splitNewDelta(rank: number): string {
  let text =
    'Mitotic Bloom — a bolt that has finished piercing and bouncing bursts into ' +
    splitShardsAt(rank) +
    ' shards at 35% damage, fanned across its heading. Shards never burst again.';
  if (rank > 1) text += ' Starts at rank ' + rank + '.';
  return text;
}

function splitRankDelta(from: number, to: number): string {
  return 'Rank ' + from + ' → ' + to + ': bursts into ' + splitShardsAt(to) + ' shards (was ' + splitShardsAt(from) + ').';
}

function cascadeNewDelta(rank: number): string {
  let text =
    'Cascade Ganglion — one bolt a volley sets off a ripple where it lands: it reaches ' +
    cascadeBodiesAt(rank) +
    ' more enemies, spreading wider and halving its damage at every level.';
  if (rank > 1) text += ' Starts at rank ' + rank + '.';
  return text;
}

function cascadeRankDelta(from: number, to: number): string {
  return (
    'Rank ' + from + ' → ' + to + ': the ripple reaches ' + cascadeBodiesAt(to) + ' enemies (was ' + cascadeBodiesAt(from) + ').'
  );
}

/** Fixed comparison order so a delta string is stable across runs. */
const WEAPON_STAT_ORDER: readonly string[] = [
  'damage',
  'contact_dps',
  'absorb_flat',
  'rate_per_s',
  'count',
  'penetration',
  'chain_targets',
  'chain_falloff_pct',
  'secondary_target_damage_pct',
  'speed_u_per_s',
  'angular_speed_deg_per_s',
  'sweep_turn_rate_deg_per_s',
  'revolution_s',
  'cooldown_s',
  'deploy_cooldown_s',
  'cast_cooldown_s',
  'per_enemy_hit_cooldown_s',
  'arm_time_s',
  'respawn_s',
  'contact_tick_s',
  'duration_s',
  'iframes_s',
  'slow_pct',
  'knockback_u',
  'area.spread_cone_deg',
  'area.cone_deg',
  'area.radius',
  'area.blast_radius',
  'area.pulse_radius',
  'area.impact_radius',
  'area.trigger_radius',
  'area.orbit_radius',
  'area.link_range',
  'area.beam_width',
  'area.beam_length',
  'area.range_u',
  'area.cast_range',
  'area.lunge_u',
  'range_u',
];

function statNum(s: WeaponStats, key: string): number | undefined {
  const dot = key.indexOf('.');
  if (dot >= 0) {
    const area = s.area as Record<string, number> | undefined;
    if (area === undefined || area === null) return undefined;
    const v = area[key.slice(dot + 1)];
    return typeof v === 'number' ? v : undefined;
  }
  const v = (s as Record<string, unknown>)[key];
  return typeof v === 'number' ? v : undefined;
}

function prettyKey(key: string): string {
  const dot = key.indexOf('.');
  const base = dot >= 0 ? key.slice(dot + 1) : key;
  return base.replace(/_/g, ' ');
}

/** One plain-language clause for a single changed weapon stat. */
function weaponClause(key: string, from: number, to: number): string {
  const up = to > from;
  switch (key) {
    case 'damage':
      return (up ? 'hits harder (' : 'hits softer (') + formatNumber(to) + ' damage, was ' + formatNumber(from) + ')';
    case 'contact_dps':
      return (up ? 'burns hotter (' : 'burns cooler (') + formatNumber(to) + ' per second, was ' + formatNumber(from) + ')';
    case 'absorb_flat':
      return (up ? 'absorbs more (' : 'absorbs less (') + formatNumber(to) + ' damage, was ' + formatNumber(from) + ')';
    case 'rate_per_s':
      return (up ? 'fires faster (' : 'fires slower (') + formatNumber(to) + '/s, was ' + formatNumber(from) + '/s)';
    case 'count':
      return (up ? 'sends out more (' : 'sends out fewer (') + formatNumber(to) + ' at once, was ' + formatNumber(from) + ')';
    case 'penetration':
      if (to < 0) return 'pierces every enemy in the line';
      return (up ? 'pierces more (' : 'pierces fewer (') + formatNumber(to) + ' enemies, was ' + formatNumber(from) + ')';
    case 'chain_targets':
      return (up ? 'chains to more enemies (' : 'chains to fewer enemies (') + formatNumber(to) + ', was ' + formatNumber(from) + ')';
    case 'chain_falloff_pct':
      return (Math.abs(to) < Math.abs(from) ? 'loses less damage per jump (' : 'loses more damage per jump (') +
        formatNumber(Math.abs(to)) + '%, was ' + formatNumber(Math.abs(from)) + '%)';
    case 'secondary_target_damage_pct':
      return (up ? 'the second target takes more (' : 'the second target takes less (') + formatNumber(to) + '%, was ' + formatNumber(from) + '%)';
    case 'speed_u_per_s':
      return (up ? 'travels faster (' : 'travels slower (') + formatNumber(to) + ' u/s, was ' + formatNumber(from) + ' u/s)';
    case 'angular_speed_deg_per_s':
      return (up ? 'orbits faster (' : 'orbits slower (') + formatNumber(to) + '°/s, was ' + formatNumber(from) + '°/s)';
    case 'sweep_turn_rate_deg_per_s':
      return (up ? 'turns faster (' : 'turns slower (') + formatNumber(to) + '°/s, was ' + formatNumber(from) + '°/s)';
    case 'revolution_s':
      return (up ? 'sweeps slower (' : 'sweeps faster (') + formatNumber(to) + ' s per turn, was ' + formatNumber(from) + ' s)';
    case 'cooldown_s':
    case 'deploy_cooldown_s':
    case 'cast_cooldown_s':
    case 'per_enemy_hit_cooldown_s':
      return (up ? 'recharges slower (' : 'recharges faster (') + formatNumber(to) + ' s, was ' + formatNumber(from) + ' s)';
    case 'arm_time_s':
      return (up ? 'arms slower (' : 'arms sooner (') + formatNumber(to) + ' s, was ' + formatNumber(from) + ' s)';
    case 'respawn_s':
      return (up ? 'returns slower (' : 'returns sooner (') + formatNumber(to) + ' s, was ' + formatNumber(from) + ' s)';
    case 'contact_tick_s':
      return (up ? 'ticks slower (' : 'ticks faster (') + formatNumber(to) + ' s, was ' + formatNumber(from) + ' s)';
    case 'duration_s':
      return (up ? 'lasts longer (' : 'cycles faster (') + formatNumber(to) + ' s, was ' + formatNumber(from) + ' s)';
    case 'iframes_s':
      return (up ? 'keeps you untouchable longer (' : 'keeps you untouchable briefly (') + formatNumber(to) + ' s, was ' + formatNumber(from) + ' s)';
    case 'slow_pct':
      return (up ? 'slows harder (' : 'slows less (') + formatNumber(to) + '%, was ' + formatNumber(from) + '%)';
    case 'knockback_u':
      return (up ? 'shoves harder (' : 'shoves less (') + formatNumber(to) + ' u, was ' + formatNumber(from) + ' u)';
    case 'area.spread_cone_deg':
    case 'area.cone_deg':
      return (up ? 'a wider fan (' : 'a tighter fan (') + formatNumber(to) + '°, was ' + formatNumber(from) + '°)';
    case 'area.radius':
    case 'area.blast_radius':
    case 'area.pulse_radius':
    case 'area.impact_radius':
      return (up ? 'a bigger blast (radius ' : 'a smaller blast (radius ') + formatNumber(to) + ', was ' + formatNumber(from) + ')';
    case 'area.trigger_radius':
      return (up ? 'triggers from farther (' : 'triggers from closer (') + formatNumber(to) + ' u, was ' + formatNumber(from) + ' u)';
    case 'area.orbit_radius':
      return (up ? 'a wider orbit (radius ' : 'a tighter orbit (radius ') + formatNumber(to) + ', was ' + formatNumber(from) + ')';
    case 'area.link_range':
      return (up ? 'links farther (' : 'links closer (') + formatNumber(to) + ' u, was ' + formatNumber(from) + ' u)';
    case 'area.beam_width':
      return (up ? 'a wider beam (' : 'a narrower beam (') + formatNumber(to) + ' u, was ' + formatNumber(from) + ' u)';
    case 'area.beam_length':
    case 'area.range_u':
    case 'area.cast_range':
    case 'range_u':
      return (up ? 'reaches farther (' : 'reaches less far (') + formatNumber(to) + ' u, was ' + formatNumber(from) + ' u)';
    case 'area.lunge_u':
      return (up ? 'lunges farther (' : 'lunges shorter (') + formatNumber(to) + ' u, was ' + formatNumber(from) + ' u)';
    default:
      return prettyKey(key) + ' ' + formatNumber(to) + ' (was ' + formatNumber(from) + ')';
  }
}

function joinClauses(list: string[]): string {
  if (list.length === 0) return '';
  if (list.length === 1) return list[0];
  let out = '';
  for (let i = 0; i < list.length - 1; i++) {
    if (i > 0) out += ', ';
    out += list[i];
  }
  return out + ' and ' + list[list.length - 1];
}

/** §13.1.6 — "Lv 3 → 4: fires faster (0.85/s, was 0.7/s) and a tighter fan (24°, was 28°)." */
export function weaponUpgradeDelta(weaponId: string, from: number, to: number): string {
  const def = weaponById.get(weaponId);
  const head = 'Lv ' + from + ' → ' + to + ': ';
  if (def === undefined) return head + 'improves this weapon.';
  const prev = resolveWeaponStats(def, from);
  const next = resolveWeaponStats(def, to);
  const clauses: string[] = [];
  for (let i = 0; i < WEAPON_STAT_ORDER.length; i++) {
    const key = WEAPON_STAT_ORDER[i];
    const a = statNum(prev, key);
    const b = statNum(next, key);
    if (a === undefined || b === undefined || a === b) continue;
    clauses.push(weaponClause(key, a, b));
  }
  if (clauses.length === 0) return head + 'improves this weapon.';
  return head + joinClauses(clauses) + '.';
}

/** §13.1.6 — "Rank 2 → 3: +24% damage (was +16%)." */
export function passiveUpgradeDelta(passiveId: string, from: number, to: number): string {
  // §9.13 states its ranks as a §6.1 absolute set-value table across five axes, so the
  // single-stat "magnitude × rank" sentence below cannot describe it.
  if (passiveId === HOLD_PASSIVE_ID) return holdRankDelta(from, to);
  if (passiveId === SPLIT_PASSIVE_ID) return splitRankDelta(from, to);
  if (passiveId === CASCADE_PASSIVE_ID) return cascadeRankDelta(from, to);
  const key = passiveStatKey(passiveId);
  const label = PASSIVE_LABELS[key] === undefined ? prettyKey(key) : PASSIVE_LABELS[key];
  const per = passiveMagnitudePerRank(passiveId);
  const unit = passiveStatUnit(passiveId);
  const suffix = unit === 'percent' ? '%' : unit === 'flat_seconds' ? ' s' : '';
  const head = 'Rank ' + from + ' → ' + to + ': ';
  return head + signed(per * to) + suffix + ' ' + label + ' (was ' + signed(per * from) + suffix + ').';
}

/** §13.1.6 first-acquisition blurb. Exported for §9.12's Graft, which grants the same items. */
export function newWeaponDelta(weaponId: string, rank: number): string {
  const def = weaponById.get(weaponId);
  const name = def === undefined ? titleFromId(weaponId) : def.display_name;
  const blurb = WEAPON_BLURBS[weaponId];
  let text: string;
  if (blurb !== undefined) {
    text = name + ' — ' + blurb;
  } else if (def !== undefined) {
    const s = def.base_stats;
    const dmg = s.damage === undefined ? 0 : s.damage;
    const rate = s.rate_per_s === undefined ? 0 : s.rate_per_s;
    text = name + ' — ' + formatNumber(dmg) + ' damage at ' + formatNumber(rate) + '/s, on its own.';
  } else {
    text = name + ' — a new weapon for an empty slot.';
  }
  if (rank > 1) text += ' Starts at Lv ' + rank + '.';
  return text;
}

/** §13.1.6 first-acquisition blurb. Exported for §9.12's Graft, which grants the same items. */
export function newPassiveDelta(passiveId: string, rank: number): string {
  if (passiveId === HOLD_PASSIVE_ID) return holdNewDelta(rank);
  if (passiveId === SPLIT_PASSIVE_ID) return splitNewDelta(rank);
  if (passiveId === CASCADE_PASSIVE_ID) return cascadeNewDelta(rank);
  const def = passiveById.get(passiveId);
  const name = def === undefined ? titleFromId(passiveId) : def.display_name;
  const key = passiveStatKey(passiveId);
  const label = PASSIVE_LABELS[key] === undefined ? prettyKey(key) : PASSIVE_LABELS[key];
  const per = passiveMagnitudePerRank(passiveId);
  const unit = passiveStatUnit(passiveId);
  const suffix = unit === 'percent' ? '%' : unit === 'flat_seconds' ? ' s' : '';
  let text = name + ' — ' + signed(per * rank) + suffix + ' ' + label + '.';
  if (rank > 1) text += ' Starts at rank ' + rank + '.';
  return text;
}

function firstSentence(text: string): string {
  const i = text.indexOf('. ');
  return i > 0 ? text.slice(0, i + 1) : text;
}

/**
 * §13.1.6 — "Kingspear-Medusa — requires Spear-Jelly Lance Lv 5 + Bristlemouth
 * Instinct. Crits split into 3 homing barbs."
 */
export function evolutionDelta(evolutionId: string, weaponId: string): string {
  const evo = evolutionById.get(evolutionId);
  const name = titleFromId(evolutionId);
  const weaponName = displayNameOf(weaponId);
  let passiveName = '';
  let behaviour = 'transforms the weapon.';
  if (evo !== undefined) {
    const req = evo.required_passive_id as string | undefined;
    if (req !== undefined) passiveName = displayNameOf(req);
    const t = evo.transformed as { behavior?: string } | undefined;
    if (t !== undefined && typeof t.behavior === 'string' && t.behavior.length > 0) {
      behaviour = firstSentence(t.behavior);
    }
  }
  const req = passiveName === '' ? '' : ' + ' + passiveName;
  return name + ' — requires ' + weaponName + ' Lv ' + MAX_WEAPON_LEVEL + req + '. ' + behaviour;
}

// --- §6.5 synergy annotation ---

function annotate(w: World, card: OfferCard): void {
  const counterPartner = counterPartnerFor(w, card.id);
  if (counterPartner !== '') {
    card.counter_synergy = true;
    const isPassiveCard = passiveStatKey(card.id) !== '';
    const weaponId = isPassiveCard ? counterPartner : card.id;
    const statKey = isPassiveCard ? passiveStatKey(card.id) : passiveStatKey(counterPartner);
    const eff = counterEfficiency(weaponId, statKey);
    card.synergy_text =
      eff === 0
        ? 'No effect on your current weapons (' + displayNameOf(counterPartner) + ').'
        : counterEffectText(card.id, counterPartner);
    return;
  }
  const partner = synergyPartnerFor(w, card.id);
  if (partner !== '') {
    card.counter_synergy = false;
    card.synergy_text = 'Works with ' + displayNameOf(partner) + ': ' + synergyEffectText(card.id, partner);
    return;
  }
  card.counter_synergy = false;
  card.synergy_text = '';
}

function makeCard(
  w: World,
  id: string,
  kind: OfferCard['kind'],
  name: string,
  from: number,
  to: number,
  delta: string,
): OfferCard {
  const card: OfferCard = {
    id,
    kind,
    display_name: name,
    level_from: from,
    level_to: to,
    delta_text: delta.length > 0 ? delta : name + ' — improves your build.',
    synergy_text: '',
    counter_synergy: false,
    icon: id,
  };
  annotate(w, card);
  return card;
}

// --- candidate pools (§9.4.1 / §9.4.2) ---

/** Scratch candidate lists; offer generation runs only while the sim is paused. */
const upgradeIds: string[] = [];
const newIds: string[] = [];

function contains(list: readonly string[], id: string): boolean {
  for (let i = 0; i < list.length; i++) {
    if (list[i] === id) return true;
  }
  return false;
}

function collectUpgrades(w: World, out: string[]): void {
  out.length = 0;
  const p = w.player;
  for (let i = 0; i < p.weapon_count; i++) {
    const slot = p.weapons[i];
    if (slot.def_id === '' || slot.evolved) continue;
    if (slot.level < MAX_WEAPON_LEVEL) out.push(slot.def_id);
  }
  for (let i = 0; i < p.passive_count; i++) {
    const slot = p.passives[i];
    if (slot.def_id === '') continue;
    if (slot.rank < passiveMaxRankOf(slot.def_id)) out.push(slot.def_id);
  }
  if (p.active !== null && p.active.def_id !== '' && weaponById.has(p.active.def_id)) {
    if (stateOf(w).active_level < MAX_WEAPON_LEVEL) out.push(p.active.def_id);
  }
}

function collectNew(w: World, out: string[]): void {
  out.length = 0;
  const p = w.player;
  if (p.weapon_count < MAX_WEAPON_SLOTS) {
    for (let i = 0; i < WEAPONS.length; i++) {
      const def = WEAPONS[i];
      if (def.slot !== 'weapon') continue;
      if (!unlocked.has(def.id)) continue;
      if (ownedWeapon(w, def.id)) continue;
      out.push(def.id);
    }
  }
  if (p.passive_count < MAX_PASSIVE_SLOTS) {
    for (let i = 0; i < PASSIVES.length; i++) {
      const def = PASSIVES[i];
      if (!unlocked.has(def.id)) continue;
      if (ownedPassive(w, def.id)) continue;
      out.push(def.id);
    }
  }
  // §9.4.2 — active-slot items enter the pool only once unlocked; owned = removed.
  if (p.active === null || p.active.def_id === '' || !weaponById.has(p.active.def_id)) {
    for (let i = 0; i < WEAPONS.length; i++) {
      const def = WEAPONS[i];
      if (def.slot !== 'active') continue;
      if (!unlocked.has(def.id)) continue;
      out.push(def.id);
    }
  }
}

function ownedWeapon(w: World, id: string): boolean {
  const p = w.player;
  for (let i = 0; i < p.weapon_count; i++) {
    if (p.weapons[i].def_id === id) return true;
  }
  return false;
}

function ownedPassive(w: World, id: string): boolean {
  const p = w.player;
  for (let i = 0; i < p.passive_count; i++) {
    if (p.passives[i].def_id === id) return true;
  }
  return false;
}

/**
 * §9.4.1 step 1 — the guaranteed evolution slot. Returns the weapon ID whose evolution
 * is eligible (max level + linked passive owned + not yet evolved), oldest slot first.
 */
export function eligibleEvolutionWeapon(w: World): string {
  const st = stateOf(w);
  if (st.evolutions_taken >= MAX_EVOLUTIONS) return '';
  const p = w.player;
  for (let i = 0; i < p.weapon_count; i++) {
    const slot = p.weapons[i];
    if (slot.def_id === '' || slot.evolved) continue;
    if (slot.level < MAX_WEAPON_LEVEL) continue;
    const def = weaponById.get(slot.def_id);
    if (def === undefined || def.evolution_id === undefined) continue;
    const linked = def.linked_passive_id;
    if (linked === undefined) continue;
    if (!ownedPassive(w, linked)) continue;
    return slot.def_id;
  }
  if (p.active !== null && p.active.def_id !== '' && st.active_level >= MAX_WEAPON_LEVEL) {
    const def = weaponById.get(p.active.def_id);
    if (def !== undefined && def.evolution_id !== undefined && def.linked_passive_id !== undefined) {
      if (ownedPassive(w, def.linked_passive_id) && !activeEvolved(w)) return p.active.def_id;
    }
  }
  return '';
}

const evolvedActives = new WeakMap<World, boolean>();

function activeEvolved(w: World): boolean {
  return evolvedActives.get(w) === true;
}

// --- offer generation ---

function fallbackCard(w: World, taken: readonly string[]): OfferCard {
  if (!contains(taken, FALLBACK_MOTE_CACHE)) {
    return makeCard(w, FALLBACK_MOTE_CACHE, 'passive', 'Mote Cache', 0, 0, 'Mote Cache — ' + FALLBACK_MOTE_CACHE_XP + ' XP right now.');
  }
  if (!contains(taken, FALLBACK_HULL_PATCH)) {
    return makeCard(
      w,
      FALLBACK_HULL_PATCH,
      'passive',
      'Hull Patch',
      0,
      0,
      'Hull Patch — restores ' + FALLBACK_HULL_PATCH_HP + ' HP now; anything over your maximum is wasted.',
    );
  }
  return makeCard(w, FALLBACK_MOTE_CACHE_2, 'passive', 'Mote Cache', 0, 0, 'Mote Cache — ' + FALLBACK_MOTE_CACHE_XP + ' XP right now.');
}

function rarityRank(w: World): number {
  const r = w.rng.loot.next();
  if (r < RARITY_COMMON) return 1;
  if (r < RARITY_RARE) return 2;
  return 3;
}

function buildUpgradeCard(w: World, id: string): OfferCard {
  const p = w.player;
  const weaponDef = weaponById.get(id);
  if (weaponDef !== undefined) {
    let level = 0;
    for (let i = 0; i < p.weapon_count; i++) {
      if (p.weapons[i].def_id === id) level = p.weapons[i].level;
    }
    if (level === 0 && p.active !== null && p.active.def_id === id) level = stateOf(w).active_level;
    const kind: OfferCard['kind'] = weaponDef.slot === 'active' ? 'active' : 'weapon';
    return makeCard(w, id, kind, weaponDef.display_name, level, level + 1, weaponUpgradeDelta(id, level, level + 1));
  }
  const passiveDef = passiveById.get(id);
  const rank = rankOf(w, id);
  const name = passiveDef === undefined ? titleFromId(id) : passiveDef.display_name;
  return makeCard(w, id, 'passive', name, rank, rank + 1, passiveUpgradeDelta(id, rank, rank + 1));
}

function rankOf(w: World, id: string): number {
  const p = w.player;
  for (let i = 0; i < p.passive_count; i++) {
    if (p.passives[i].def_id === id) return p.passives[i].rank;
  }
  return 0;
}

function buildNewCard(w: World, id: string, rank: number): OfferCard {
  const weaponDef = weaponById.get(id);
  if (weaponDef !== undefined) {
    const kind: OfferCard['kind'] = weaponDef.slot === 'active' ? 'active' : 'weapon';
    return makeCard(w, id, kind, weaponDef.display_name, 0, rank, newWeaponDelta(id, rank));
  }
  const passiveDef = passiveById.get(id);
  const name = passiveDef === undefined ? titleFromId(id) : passiveDef.display_name;
  return makeCard(w, id, 'passive', name, 0, rank, newPassiveDelta(id, rank));
}

/**
 * §9.4.1 — 3 cards: a guaranteed evolution slot when one is eligible, then weighted
 * upgrade/new draws with rarity rolls on new items. Consumes `loot` only, in order.
 */
export function generateOffers(w: World, count: number): OfferCard[] {
  const st = stateOf(w);
  const cards: OfferCard[] = [];
  const taken: string[] = [];

  const evoWeapon = eligibleEvolutionWeapon(w);
  if (evoWeapon !== '') {
    const def = weaponById.get(evoWeapon);
    const evoId = def === undefined ? '' : (def.evolution_id as string);
    if (evoId !== '') {
      cards.push(makeCard(w, evoId, 'evolution', titleFromId(evoId), MAX_WEAPON_LEVEL, MAX_WEAPON_LEVEL, evolutionDelta(evoId, evoWeapon)));
      taken.push(evoId);
    }
  }

  collectUpgrades(w, upgradeIds);
  collectNew(w, newIds);

  // §9.4.3 — exclusions only apply while at least `count` other legal entries remain.
  let excludedActive = false;
  if (st.excluded.length > 0) {
    let remaining = 0;
    for (let i = 0; i < upgradeIds.length; i++) {
      if (!contains(st.excluded, upgradeIds[i])) remaining++;
    }
    for (let i = 0; i < newIds.length; i++) {
      if (!contains(st.excluded, newIds[i])) remaining++;
    }
    excludedActive = remaining >= count;
  }

  while (cards.length < count) {
    let upCount = 0;
    for (let i = 0; i < upgradeIds.length; i++) {
      const id = upgradeIds[i];
      if (contains(taken, id)) continue;
      if (excludedActive && contains(st.excluded, id)) continue;
      upCount++;
    }
    let newCount = 0;
    for (let i = 0; i < newIds.length; i++) {
      const id = newIds[i];
      if (contains(taken, id)) continue;
      if (excludedActive && contains(st.excluded, id)) continue;
      newCount++;
    }
    if (upCount === 0 && newCount === 0) {
      const fb = fallbackCard(w, taken);
      cards.push(fb);
      taken.push(fb.id);
      continue;
    }

    // §9.4.1 step 2 — 65 upgrade / 35 new; with slots full, upgrades take all the weight.
    const roll = w.rng.loot.next();
    const useUpgrade = newCount === 0 ? true : upCount === 0 ? false : roll < WEIGHT_UPGRADE;

    if (useUpgrade) {
      let pick = w.rng.loot.next_int(upCount);
      for (let i = 0; i < upgradeIds.length; i++) {
        const id = upgradeIds[i];
        if (contains(taken, id)) continue;
        if (excludedActive && contains(st.excluded, id)) continue;
        if (pick-- === 0) {
          cards.push(buildUpgradeCard(w, id));
          taken.push(id);
          break;
        }
      }
    } else {
      let pick = w.rng.loot.next_int(newCount);
      for (let i = 0; i < newIds.length; i++) {
        const id = newIds[i];
        if (contains(taken, id)) continue;
        if (excludedActive && contains(st.excluded, id)) continue;
        if (pick-- === 0) {
          // §9.4.1 step 3 — rarity applies to NEW items only.
          const rank = rarityRank(w);
          cards.push(buildNewCard(w, id, rank));
          taken.push(id);
          break;
        }
      }
    }
  }

  return cards;
}

function emitOffer(w: World, cards: readonly OfferCard[], rerollIndex: number): void {
  const options: string[] = [];
  for (let i = 0; i < cards.length; i++) options.push(cards[i].id);
  w.bus.emit('levelup_offer', { level: w.player.level, options, reroll_index: rerollIndex });
}

function openOffers(w: World): void {
  const st = stateOf(w);
  st.excluded.length = 0;
  st.rerolled_this_pause = false;
  st.offers = generateOffers(w, cfg.get('progression.offer_count'));
  emitOffer(w, st.offers, w.player.rerolls_used);
  w.paused = true;
}

/**
 * §9.4.3 — one reroll per pause, ≤ 3 per run, no refunds. Returns the new draw, or
 * null when no draw is open or the budget is spent.
 */
export function rerollOffers(w: World): OfferCard[] | null {
  const st = stateOf(w);
  if (st.offers === null) return null;
  if (st.rerolled_this_pause) return null;
  if (w.player.rerolls_used >= cfg.get('progression.reroll_max')) return null;

  st.excluded.length = 0;
  for (let i = 0; i < st.offers.length; i++) st.excluded.push(st.offers[i].id);
  w.player.rerolls_used++;
  st.rerolled_this_pause = true;
  st.offers = generateOffers(w, cfg.get('progression.offer_count'));
  emitOffer(w, st.offers, w.player.rerolls_used);
  return st.offers;
}

// --- applying a pick ---

function grantWeapon(w: World, id: string, level: number): void {
  const p = w.player;
  for (let i = 0; i < p.weapon_count; i++) {
    if (p.weapons[i].def_id === id) {
      p.weapons[i].level = level;
      return;
    }
  }
  if (p.weapon_count >= MAX_WEAPON_SLOTS) return;
  const def = weaponById.get(id);
  const slot = p.weapons[p.weapon_count];
  slot.def_id = id;
  slot.level = level;
  slot.evolved = false;
  slot.cooldown_until = 0;
  slot.last_target_id = 0;
  slot.policy = def === undefined ? 'nearest' : def.targeting_policy;
  slot.phase = 0;
  p.weapon_count++;
}

function grantPassive(w: World, id: string, rank: number): void {
  const p = w.player;
  for (let i = 0; i < p.passive_count; i++) {
    if (p.passives[i].def_id === id) {
      p.passives[i].rank = rank;
      return;
    }
  }
  if (p.passive_count >= MAX_PASSIVE_SLOTS) return;
  const slot = p.passives[p.passive_count];
  slot.def_id = id;
  slot.rank = rank;
  p.passive_count++;
}

function grantEvolution(w: World, evolutionId: string): void {
  const st = stateOf(w);
  const evo = evolutionById.get(evolutionId);
  const source = evo === undefined ? '' : ((evo.source_weapon_id as string) ?? '');
  const p = w.player;
  for (let i = 0; i < p.weapon_count; i++) {
    if (p.weapons[i].def_id === source) {
      p.weapons[i].evolved = true;
      p.weapons[i].level = MAX_WEAPON_LEVEL;
      st.evolutions_taken++;
      w.bus.emit('evolution', { weapon_id: source, evolution_id: evolutionId });
      return;
    }
  }
  if (p.active !== null && p.active.def_id === source) {
    evolvedActives.set(w, true);
    st.evolutions_taken++;
    w.bus.emit('evolution', { weapon_id: source, evolution_id: evolutionId });
  }
}

/**
 * §9.12 — the Graft grants the same items the level-up screen grants, through the same
 * three functions, so slot limits, the max-level clamp and the evolution counter have
 * exactly one implementation. `level` / `rank` are absolute (§6.1 set-values), and a
 * grant into a full slot family is a no-op, not an overflow.
 */
export function grantWeaponLevel(w: World, id: string, level: number): void {
  grantWeapon(w, id, Math.min(level, MAX_WEAPON_LEVEL));
}

export function grantPassiveRank(w: World, id: string, rank: number): void {
  grantPassive(w, id, Math.min(rank, passiveMaxRankOf(id)));
}

export function grantEvolutionById(w: World, evolutionId: string): void {
  if (stateOf(w).evolutions_taken >= MAX_EVOLUTIONS) return;
  grantEvolution(w, evolutionId);
}

/**
 * Apply one level-up choice. Advances to the next queued offer when several levels
 * were banked in the same tick, otherwise unpauses the sim.
 */
export function applyPick(w: World, optionId: string): void {
  const st = stateOf(w);
  const offers = st.offers;
  if (offers === null) return;
  let card: OfferCard | null = null;
  for (let i = 0; i < offers.length; i++) {
    if (offers[i].id === optionId) card = offers[i];
  }
  if (card === null) return;

  const options: string[] = [];
  for (let i = 0; i < offers.length; i++) options.push(offers[i].id);
  w.bus.emit('levelup_pick', { level: w.player.level, picked: optionId, options });

  if (card.id === FALLBACK_MOTE_CACHE || card.id === FALLBACK_MOTE_CACHE_2) {
    w.player.xp += FALLBACK_MOTE_CACHE_XP;
  } else if (card.id === FALLBACK_HULL_PATCH) {
    w.player.hp = Math.min(w.player.max_hp, w.player.hp + FALLBACK_HULL_PATCH_HP);
  } else if (card.kind === 'evolution') {
    grantEvolution(w, card.id);
  } else if (card.kind === 'active') {
    const p = w.player;
    if (p.active === null || p.active.def_id !== card.id) {
      p.active = { def_id: card.id, cooldown_until: 0 };
    }
    st.active_level = card.level_to;
  } else if (card.kind === 'weapon') {
    grantWeapon(w, card.id, card.level_to);
  } else if (passiveById.has(card.id)) {
    grantPassive(w, card.id, card.level_to);
  }

  recomputeStats(w);

  st.pending_levels--;
  if (st.pending_levels > 0) {
    openOffers(w);
  } else {
    st.pending_levels = 0;
    st.offers = null;
    st.excluded.length = 0;
    st.rerolled_this_pause = false;
    w.paused = false;
  }
}

/**
 * Step 13. Drains banked XP into levels and opens the choice modal.
 * Zero allocation in the common path (no threshold crossed).
 */
export function stepProgression(w: World): void {
  const st = stateOf(w);
  if (!st.initialized) {
    st.initialized = true;
    if (w.player.xp_to_next <= 0) w.player.xp_to_next = xpRequired(w.player.level);
    recomputeStats(w);
  }
  // Step 13a — §9.13. Runs before the XP drain so the meter is current when a level-up
  // pauses the world, and early-outs entirely on a build without the line.
  stepHold(w);
  const p = w.player;
  let guard = 0;
  while (p.xp >= p.xp_to_next && guard++ < 64) {
    p.xp -= p.xp_to_next;
    p.level++;
    p.xp_to_next = xpRequired(p.level);
    st.pending_levels++;
    w.bus.emit('level_up', { level: p.level });
  }
  if (st.pending_levels > 0 && st.offers === null) openOffers(w);
}

/** Total evolutions defined in content (§6.7: 1:1 with weapons). */
export const EVOLUTION_COUNT = EVOLUTIONS.length;
